import { Search } from "lucide-react";
import { Link } from "@/lib/nav";
import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { cn } from "@/lib/utils";
import { isPaletteShortcut, movePaletteIndex, paletteEntries, paletteToggle } from "@/shell/search-palette-items";

// 떠 있는 Radix 창(Dialog·AlertDialog·Sheet) — 이 중 하나가 열려 있으면 단축키로 팔레트를 겹쳐 열지 않는다.
const OPEN_MODAL_SELECTOR = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

const isMacPlatform = (): boolean => /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

// 헤더 찾기 버튼과 Cmd/Ctrl+K 로 여는 이동 창 — 메뉴로 가거나 목록 검색(?q=)으로 넘긴다.
const SearchPalette = () => {
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const entries = useMemo(() => paletteEntries(query), [query]);

  const changeOpen = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) return;
    setQuery("");
    setActive(0);
  }, []);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!isPaletteShortcut(e, isMacPlatform())) return;
      const next = paletteToggle(open, document.querySelector(OPEN_MODAL_SELECTOR) !== null);
      if (next === null) return;
      e.preventDefault();
      changeOpen(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, changeOpen]);

  const optionId = (i: number) => `${listId}-${i}`;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => movePaletteIndex(i, e.key === "ArrowDown" ? 1 : -1, entries.length));
      return;
    }
    if (e.key !== "Enter") return;
    e.preventDefault();
    // router.push 대신 고른 링크를 누른다 — 편집 화면의 떠나기 확인(useLeaveGuard)이 링크 누름만 가로챈다.
    listRef.current?.querySelector<HTMLAnchorElement>("a[data-active]")?.click();
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <Button
        aria-keyshortcuts="Meta+K Control+K"
        aria-label="메뉴·목록 찾기"
        data-testid="search-palette-open"
        size="sm"
        type="button"
        variant="ghost-muted"
        onClick={() => changeOpen(true)}>
        <Search className="size-4" />
        <span className="hidden sm:inline">찾기</span>
        <span className="hidden rounded-sm border border-border px-1 text-xs lg:inline">⌘K</span>
      </Button>
      <DialogContent className="max-w-md gap-3 p-4" data-testid="search-palette" isCloseIcon={false}>
        <DialogTitle className="text-sm">찾기</DialogTitle>
        <DialogDescription className="text-xs">메뉴로 가거나 고객·견적·계약·현장 목록에서 찾습니다</DialogDescription>
        <Input
          autoFocus
          aria-activedescendant={entries.length > 0 ? optionId(active) : undefined}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded
          aria-label="찾을 말"
          autoComplete="off"
          role="combobox"
          data-testid="search-palette-input"
          inputSize="sm"
          name="palette-q"
          placeholder="메뉴 이름 · 고객 이름 · 견적번호…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        <ul
          ref={listRef}
          aria-label="찾기 결과"
          className="flex max-h-80 flex-col gap-y-0.5 overflow-y-auto"
          data-testid="search-palette-list"
          id={listId}
          role="listbox">
          {entries.map((entry, i) => (
            <li key={entry.key} role="none">
              <Link
                aria-selected={i === active}
                className={cn(
                  "flex items-center justify-between gap-x-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent",
                  i === active && "bg-accent text-accent-foreground"
                )}
                data-active={i === active || undefined}
                href={entry.href}
                id={optionId(i)}
                role="option"
                onClick={() => setOpen(false)}
                onMouseEnter={() => setActive(i)}>
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{entry.label}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{entry.hint}</span>
              </Link>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

export default SearchPalette;
